import type { CompanyProfile, DutyTablePresetId, FtlSchemeId } from "./types";
import {
  DUTY_LIMIT_PRESETS,
  formatDutyHours,
  type DutyCertificateKind,
  type DutyLimits,
  type DutyRoster,
  type DutySettings,
} from "./duty";

/** Stored on company. */
export const FTL_SCHEME_IDS: readonly FtlSchemeId[] = ["car704", "far135", "easa"];

export const DEFAULT_FTL_SCHEME: FtlSchemeId = "car704";

export const DUTY_TABLE_PRESET_IDS: readonly DutyTablePresetId[] = [
  "car703",
  "car704",
  "car705",
  "far135-single",
  "far135-two",
  "far135-scheduled",
  "easa-basic",
  "easa-late",
  "easa-augmented",
];

/**
 * Best-fit preset when the scheme changes and we are not resuming a CAR certificate.
 * CAR → 704 Commuter (Celtic / Hawker company ops, not the first row 703).
 * FAR → two-pilot (Hawker is not a single-pilot airplane; single-pilot is listed first).
 * EASA → basic day (first / baseline unaugmented table).
 */
export const DEFAULT_DUTY_PRESET_BY_SCHEME: Record<FtlSchemeId, DutyTablePresetId> = {
  car704: "car704",
  far135: "far135-two",
  easa: "easa-basic",
};

export const SIM_FTL_NOTE =
  "Simplified sim FTL — not legal advice. Not a Transport Canada, FAA, or EASA approval. The in-force clock follows the preset you pick.";

export type FtlSchemeOption = {
  id: FtlSchemeId;
  label: string;
  can: string;
  cant: string;
};

export const FTL_SCHEME_OPTIONS: readonly FtlSchemeOption[] = [
  {
    id: "car704",
    label: "CAR (Canada)",
    can: "Runs the duty clock on the 703 / 704 / 705 training presets.",
    cant: "Not a Transport Canada approval and not a full CAR table. No weekly lookback, report time, deadhead, or standby credit.",
  },
  {
    id: "far135",
    label: "FAR (USA)",
    can: "Switches the in-force clock to simplified FAR 135 daily ballparks: single-pilot, two-pilot, or a shorter scheduled-feeling day.",
    cant: "Not FAA legal tables and not Part 121. No 8-in-24, 100-in-672, or annual totals. Does not change the CAR 704 company certificate.",
  },
  {
    id: "easa",
    label: "EASA (EU)",
    can: "Switches the in-force clock to simplified daily ballparks: basic day, late report, or a longer augmented-crew day.",
    cant: "Not an EASA approval. No sector cuts, WOCL, acclimatisation, or in-flight rest credit. The flight number is a sim stand-in. Company ops stay CAR.",
  },
];

export type DutyTablePreset = {
  id: DutyTablePresetId;
  scheme: FtlSchemeId;
  label: string;
  can: string;
  cant: string;
  limits: DutyLimits;
  /** Set only for CAR presets. FAR / EASA leave the company certificate alone. */
  certificateKind?: DutyCertificateKind;
};

const warn = 0.8;

export const DUTY_TABLE_PRESETS: readonly DutyTablePreset[] = [
  {
    id: "car703",
    scheme: "car704",
    label: "703 Air Taxi",
    can: "Tighter daily flight (8:00) for an air-taxi-shaped day. The clock uses these hours.",
    cant: "Real CAR 703 excludes turbo-jets, so a Hawker is not a 703 airplane. No weekly totals. Not a TC approval.",
    limits: { ...DUTY_LIMIT_PRESETS["703"] },
    certificateKind: "703",
  },
  {
    id: "car704",
    scheme: "car704",
    label: "704 Commuter",
    can: "14:00 FDP, 8:30 flight, 10:00 rest on the duty clock.",
    cant: "Not a full CAR 704 table. No 7-day lookback, split duty, or standby credit. Not a TC approval.",
    limits: { ...DUTY_LIMIT_PRESETS["704"] },
    certificateKind: "704",
  },
  {
    id: "car705",
    scheme: "car704",
    label: "705 Airline",
    can: "Shorter FDP (13:00) and longer rest (12:00) for an airline-shaped day.",
    cant: "Not a 705 airline. No augmented crew, reserve, or monthly caps. Not a TC approval.",
    limits: { ...DUTY_LIMIT_PRESETS["705"] },
    certificateKind: "705",
  },
  {
    id: "far135-single",
    scheme: "far135",
    label: "FAR 135 single-pilot (sim)",
    can: "One-pilot day: 14:00 FDP, 8:00 flight, 10:00 rest. The in-force clock leaves the CAR table.",
    cant: "Not §135.267. No 8-in-24 lookback. A Hawker is not single-pilot. Not FAA legal advice.",
    limits: { maxFdpMin: 14 * 60, maxFlightTimeMin: 8 * 60, minRestMin: 10 * 60, warnRatio: warn },
  },
  {
    id: "far135-two",
    scheme: "far135",
    label: "FAR 135 two-pilot (sim)",
    can: "Two-pilot day: 14:00 FDP, 10:00 flight, 10:00 rest. Default FAR pick — the Hawker needs two pilots.",
    cant: "Not the FAA two-pilot table. No lookbacks or reserve rules. Not legal advice. Company certificate stays CAR.",
    limits: { maxFdpMin: 14 * 60, maxFlightTimeMin: 10 * 60, minRestMin: 10 * 60, warnRatio: warn },
  },
  {
    id: "far135-scheduled",
    scheme: "far135",
    label: "FAR 135 scheduled-feeling (sim)",
    can: "Shorter day: 12:00 FDP, 8:00 flight, 9:00 rest, for a scheduled-feeling desk.",
    cant: "Not Part 121 and not the real scheduled 135 rest matrix. Not legal advice.",
    limits: { maxFdpMin: 12 * 60, maxFlightTimeMin: 8 * 60, minRestMin: 9 * 60, warnRatio: warn },
  },
  {
    id: "easa-basic",
    scheme: "easa",
    label: "EASA basic day (sim)",
    can: "Unaugmented daytime shape: 13:00 FDP, 9:00 flight stand-in, 12:00 rest.",
    cant: "No report-time table or sector cuts. EASA limits FDP, not a simple daily flight total. Not legal advice.",
    limits: { maxFdpMin: 13 * 60, maxFlightTimeMin: 9 * 60, minRestMin: 12 * 60, warnRatio: warn },
  },
  {
    id: "easa-late",
    scheme: "easa",
    label: "EASA late report (sim)",
    can: "Shorter late-day shape: 11:00 FDP, 8:00 flight stand-in, 12:00 rest.",
    cant: "Not the window-of-circadian-low table. Not legal advice. Company ops stay CAR.",
    limits: { maxFdpMin: 11 * 60, maxFlightTimeMin: 8 * 60, minRestMin: 12 * 60, warnRatio: warn },
  },
  {
    id: "easa-augmented",
    scheme: "easa",
    label: "EASA augmented (sim)",
    can: "Longer day when you pretend a relief pilot is aboard: 15:00 FDP, 10:00 flight, 14:00 rest.",
    cant: "No rest-facility class and no bunk credit. Not a real augmented table. Not legal advice.",
    limits: { maxFdpMin: 15 * 60, maxFlightTimeMin: 10 * 60, minRestMin: 14 * 60, warnRatio: warn },
  },
];

const PRESET_BY_ID = new Map(DUTY_TABLE_PRESETS.map((row) => [row.id, row]));

export function isFtlSchemeId(value: unknown): value is FtlSchemeId {
  return value === "car704" || value === "far135" || value === "easa";
}

export function isDutyTablePresetId(value: unknown): value is DutyTablePresetId {
  return typeof value === "string" && PRESET_BY_ID.has(value as DutyTablePresetId);
}

export function hydrateFtlScheme(raw?: unknown): FtlSchemeId {
  return isFtlSchemeId(raw) ? raw : DEFAULT_FTL_SCHEME;
}

export function hydrateDutyTablePresetId(raw?: unknown, scheme?: FtlSchemeId): DutyTablePresetId {
  if (isDutyTablePresetId(raw)) return raw;
  return defaultDutyPresetForScheme(scheme ?? DEFAULT_FTL_SCHEME);
}

export function ftlSchemeLabel(id: FtlSchemeId): string {
  return FTL_SCHEME_OPTIONS.find((row) => row.id === id)?.label ?? FTL_SCHEME_OPTIONS[0]!.label;
}

export function ftlSchemeOption(id: FtlSchemeId): FtlSchemeOption {
  return FTL_SCHEME_OPTIONS.find((row) => row.id === id) ?? FTL_SCHEME_OPTIONS[0]!;
}

export function ftlSchemeCan(id: FtlSchemeId): string {
  return ftlSchemeOption(id).can;
}

export function ftlSchemeCant(id: FtlSchemeId): string {
  return ftlSchemeOption(id).cant;
}

export function ftlSchemeHint(id: FtlSchemeId): string {
  const row = ftlSchemeOption(id);
  return `Can: ${row.can} Can’t: ${row.cant}`;
}

export function dutyTablePresetById(id: DutyTablePresetId): DutyTablePreset {
  return PRESET_BY_ID.get(id) ?? PRESET_BY_ID.get("car704")!;
}

export function dutyPresetsForScheme(scheme: FtlSchemeId): DutyTablePreset[] {
  return DUTY_TABLE_PRESETS.filter((row) => row.scheme === scheme);
}

export function defaultDutyPresetForScheme(scheme: FtlSchemeId): DutyTablePresetId {
  return DEFAULT_DUTY_PRESET_BY_SCHEME[scheme] ?? "car704";
}

export function dutyPresetNumbers(preset: DutyTablePreset): string {
  const limits = preset.limits;
  return `${formatDutyHours(limits.maxFdpMin)} FDP / ${formatDutyHours(limits.maxFlightTimeMin)} flight / ${formatDutyHours(limits.minRestMin)} rest`;
}

/** Duty numbers for the scheme’s best-fit preset. FAR / EASA are not the CAR 704 table. */
export function ftlSchemeLimits(id: FtlSchemeId): DutyLimits {
  return { ...dutyTablePresetById(defaultDutyPresetForScheme(id)).limits };
}

/**
 * Preset to select when the scheme dropdown changes.
 * CAR resumes 703 / 704 / 705 from the company certificate. FAR and EASA take the best-fit default.
 */
export function presetForSchemeChange(
  scheme: FtlSchemeId,
  duty: Pick<DutySettings, "certificateKind">,
): DutyTablePresetId {
  if (scheme === "car704") {
    if (duty.certificateKind === "703") return "car703";
    if (duty.certificateKind === "705") return "car705";
    return "car704";
  }
  return defaultDutyPresetForScheme(scheme);
}

/** Apply one preset. CAR presets also write the company certificate. FAR / EASA do not. */
export function applyDutyTablePreset(id: DutyTablePresetId, current: DutySettings): DutySettings {
  const preset = isDutyTablePresetId(id) ? dutyTablePresetById(id) : null;
  if (!preset) return current;
  const next: DutySettings = {
    ...current,
    dutyTablePresetId: preset.id,
    clockLabel: preset.certificateKind ? current.certificateLabel : preset.label,
    limits: { ...preset.limits },
  };
  if (preset.certificateKind) {
    next.certificateKind = preset.certificateKind;
    next.certificateLabel = preset.label;
    next.clockLabel = preset.label;
    next.certificateUserSet = true;
  }
  return next;
}

/** Scheme dropdown: pick the matching default (CAR resumes the certificate) and apply its hours. */
export function selectFtlScheme(scheme: FtlSchemeId, duty: DutySettings): DutySettings {
  const id = presetForSchemeChange(hydrateFtlScheme(scheme), duty);
  return applyDutyTablePreset(id, duty);
}

/** Reset button: scheme best-fit. CAR always returns to 704 Commuter, not the previous 703 / 705 pick. */
export function resetDutyTableForScheme(scheme: FtlSchemeId, duty: DutySettings): DutySettings {
  return applyDutyTablePreset(defaultDutyPresetForScheme(hydrateFtlScheme(scheme)), duty);
}

function sameHours(a: DutyLimits, b: DutyLimits): boolean {
  return a.maxFdpMin === b.maxFdpMin && a.maxFlightTimeMin === b.maxFlightTimeMin && a.minRestMin === b.minRestMin;
}

function limitsMatchCarStock(limits: DutyLimits): boolean {
  return (["703", "704", "705"] as const).some((kind) => sameHours(limits, DUTY_LIMIT_PRESETS[kind]));
}

function presetBelongs(id: unknown, scheme: FtlSchemeId): id is DutyTablePresetId {
  return isDutyTablePresetId(id) && dutyTablePresetById(id).scheme === scheme;
}

/**
 * Which preset is in force. Matching saved ids win. If they disagree, an
 * explicit company pick wins unless the company id is only the scheme default
 * (that fill-in must not hide a duty-roster pick or a CAR 703 / 705 certificate).
 */
export function resolveDutyTablePresetId(
  scheme: FtlSchemeId,
  companyPresetId: unknown,
  duty: Pick<DutySettings, "dutyTablePresetId" | "certificateKind">,
): DutyTablePresetId {
  const dutyId = duty.dutyTablePresetId;
  const companyId = companyPresetId;
  if (presetBelongs(dutyId, scheme) && presetBelongs(companyId, scheme)) {
    if (dutyId === companyId) return dutyId;
    // A missing company id hydrates to the scheme default. Keep an explicit duty pick
    // (including a CAR 703 / 705 certificate) instead of that fill-in.
    if (companyId === defaultDutyPresetForScheme(scheme)) return dutyId;
    return companyId;
  }
  if (presetBelongs(companyId, scheme)) return companyId;
  if (presetBelongs(dutyId, scheme)) return dutyId;
  return presetForSchemeChange(scheme, duty);
}

export function inForcePreset(
  company: Pick<CompanyProfile, "ftlScheme" | "dutyTablePresetId">,
  duty?: Pick<DutySettings, "dutyTablePresetId" | "certificateKind">,
): DutyTablePreset {
  const scheme = hydrateFtlScheme(company.ftlScheme);
  const id = resolveDutyTablePresetId(scheme, company.dutyTablePresetId, {
    dutyTablePresetId: duty?.dutyTablePresetId,
    certificateKind: duty?.certificateKind ?? "704",
  });
  return dutyTablePresetById(id);
}

/**
 * Load path. Keeps custom hour edits. If FAR / EASA is selected and the hours
 * are still a stock CAR preset, replace them — that was the silent CAR clock.
 * Does not rewrite the CAR company certificate.
 */
export function reconcileInForceDuty(
  company: CompanyProfile,
  duty: DutySettings,
): { company: CompanyProfile; duty: DutySettings } {
  const scheme = hydrateFtlScheme(company.ftlScheme);
  const preset = dutyTablePresetById(
    resolveDutyTablePresetId(scheme, company.dutyTablePresetId, duty),
  );
  const stockCar = limitsMatchCarStock(duty.limits);
  const onPreset = sameHours(duty.limits, preset.limits);
  const replaceSilentCar = preset.scheme !== "car704" && stockCar && !onPreset;
  const clockLabel =
    preset.certificateKind && duty.certificateKind === preset.certificateKind
      ? duty.certificateLabel || preset.label
      : preset.label;
  return {
    company: { ...company, ftlScheme: scheme, dutyTablePresetId: preset.id },
    duty: {
      ...duty,
      dutyTablePresetId: preset.id,
      clockLabel,
      limits: replaceSilentCar ? { ...preset.limits } : { ...duty.limits },
    },
  };
}

export function stampInForceDuty<T extends { company: CompanyProfile; duty: DutyRoster }>(row: T): T {
  const aligned = reconcileInForceDuty(row.company, row.duty.settings);
  return {
    ...row,
    company: aligned.company,
    duty: { ...row.duty, settings: aligned.duty },
  };
}

export const ENFORCE_DUTY_CLOCK_HINT =
  "On: Brain / Classic / manual schedule treat rest and duty as hard blocks, using the in-force table for the selected FTL scheme. Off: that same clock is advisory only. It does not fall back to CAR 704 hours.";
